import { AsyncPipe, DOCUMENT, NgClass, NgIf, NgTemplateOutlet } from "@angular/common";
import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    OnInit,
    Signal,
    ViewEncapsulation,
    WritableSignal,
    inject,
    signal,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { NavigationEnd, Router, RouterLink } from "@angular/router";

import { debounceTime, Observable } from "rxjs";
import { filter } from "rxjs/operators";

import { Actions, Store } from "@ngxs/store";

import { ButtonModule } from "primeng/button";
import { SkeletonModule } from "primeng/skeleton";
import { TooltipModule } from "primeng/tooltip";

import { Nullable } from "@angular-ru/cdk/typings";

import { Period } from "@core/classes/period.class";
import { SubscriberClass } from "@core/classes/subscriber.class";
import { HeaderTypeOfInformationShown } from "@core/components/header/enums/header.enum";
import { CookieName } from "@core/enums/defaults.enum";
import { generateLoader, getShortYear, hasActionsRunning } from "@core/helpers/handlers.helper";
import { Nulled } from "@core/interfaces/core.interface";
import { ICurrentUser } from "@core/interfaces/current-user.interface";
import { IPeriod } from "@core/interfaces/period.interface";
import { SliceYearPipe } from "@core/pipes/slice-year.pipe";
import { CookieService } from "@core/services/cookie.service";
import { ScreenWidthDetectorService } from "@core/services/screen-width-detector.service";
import { GlobalActions } from "@core/store/core.actions";
import { CoreStateSelector } from "@core/store/core.selectors";

import { AuthPageActions } from "../../../auth-page/store/auth.actions";
import { SpinnerComponent } from "../spinner";
import { QuarterRadioComponent } from "./quarter-radio/quarter-radio.component";
import { YearInputComponent } from "./year-input/year-input.component";

@Component({
    standalone: true,
    selector: "ruts-header",
    templateUrl: "./header.component.html",
    styleUrls: ["./header.component.scss"],
    encapsulation: ViewEncapsulation.None,
    imports: [
        NgIf,
        NgClass,
        NgTemplateOutlet,
        AsyncPipe,
        FormsModule,
        RouterLink,

        // PrimeNG
        ButtonModule,
        SkeletonModule,
        SpinnerComponent,
        TooltipModule,

        // standalone components
        SliceYearPipe,
        QuarterRadioComponent,
        YearInputComponent,
    ],
})
export class HeaderComponent extends SubscriberClass implements OnInit {
    readonly #cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
    readonly #actions$: Actions = inject(Actions);
    readonly #store: Store = inject(Store);
    readonly #router: Router = inject(Router);
    readonly #eRef: ElementRef<HTMLElement> = inject(ElementRef);
    readonly #swd: ScreenWidthDetectorService = inject(ScreenWidthDetectorService);
    readonly #document: Document = inject<Document>(DOCUMENT);
    readonly #cookies: CookieService = inject(CookieService);

    public readonly swdIsDesktop: Signal<Nullable<boolean>> = this.#swd.isDesktop;
    public readonly swdIsMobile: Signal<Nullable<boolean>> = this.#swd.isMobile;
    public readonly swdIsSmallTablet: Signal<Nullable<boolean>> = this.#swd.isSmallTablet;
    public readonly swdIsTablet: Signal<Nullable<boolean>> = this.#swd.isTablet;
    public readonly swdIsCustomLg: Signal<Nullable<boolean>> = this.#swd.isCustomLg;
    public readonly isImpersonating: WritableSignal<boolean> = signal(false);

    quarter: number;
    yearUI: number;
    yearInput: number;
    blackOutActive: boolean = false;
    periodViewShown: boolean = false;
    userInfoIsOpen: boolean = false;
    selectionPeriodPanelIsOpen: boolean;
    HeaderTypeOfInformationShown = HeaderTypeOfInformationShown;

    period$: Observable<IPeriod> = this.#store.select(CoreStateSelector.getActivePeriod);
    currentUser$: Observable<Nulled<ICurrentUser>> = this.#store.select(CoreStateSelector.getUser);

    isLoading$: Observable<boolean>;
    isUpdatingLimits$: Observable<boolean>;

    constructor() {
        super();
    }

    @HostListener("document:click", ["$event"])
    clickOutside(event: MouseEvent): void {
        if (!this.#eRef.nativeElement.contains(event.target as Node)) {
            this.hideAllPopup();
        }
    }

    ngOnInit(): void {
        this.isLoading$ = hasActionsRunning(this.#actions$, [GlobalActions.AppInit]);
        this.isUpdatingLimits$ = hasActionsRunning(this.#actions$, [
            GlobalActions.SetActivePeriod,
            GlobalActions.UpdateLimits,
        ]);

        this.isImpersonating.set(this.#cookies.has(CookieName.Impersonation));

        this.addSub([
            this.#store
                .select(CoreStateSelector.getActivePeriod)
                .pipe(
                    filter(
                        period =>
                            !Period.areEqualPeriods(period, {
                                quarter: this.quarter,
                                year: this.yearInput,
                            })
                    ),
                    debounceTime(500)
                )
                .subscribe(period => {
                    this.yearInput = period.year;
                    this.yearUI = getShortYear(period.year);
                    this.quarter = period.quarter;
                    this.#cdr.detectChanges();
                }),
            this.#router.events
                .pipe(filter(e => e instanceof NavigationEnd))
                .subscribe(() => this.hideAllPopup()),
        ]);
    }

    protected readonly loadingReveal$: Observable<boolean> = generateLoader(
        this.#actions$,
        [AuthPageActions.Reveal],
        false
    );

    openMenu() {
        const menu = this.#document.querySelector(".ruts-menu");

        menu?.classList.add("active");

        this.blackOutActive = true;
    }

    openChoosePeriod() {
        this.blackOutActive = true;
        this.userInfoIsOpen = false;
        this.selectionPeriodPanelIsOpen = true;
    }

    openUserInfo() {
        this.blackOutActive = true;
        this.selectionPeriodPanelIsOpen = false;
        this.userInfoIsOpen = true;
    }

    togglePeriodView(field: HeaderTypeOfInformationShown) {
        if (field === HeaderTypeOfInformationShown.Period) {
            this.periodViewShown = true;

            return this.openChoosePeriod();
        }

        if (field === HeaderTypeOfInformationShown.Info) {
            this.periodViewShown = false;
            if (!this.userInfoIsOpen) {
                return this.openUserInfo();
            }
            this.userInfoIsOpen = false;
        }
    }

    hideAllPopup() {
        const menu = this.#document.querySelector(".ruts-menu");

        this.blackOutActive = false;
        this.userInfoIsOpen = false;
        this.periodViewShown = false;
        this.selectionPeriodPanelIsOpen = false;

        menu?.classList.remove("active");
    }

    setNewPeriod() {
        this.hideAllPopup();

        this.#store.dispatch(
            new GlobalActions.SetActivePeriod(
                Period.create({ quarter: this.quarter, year: this.yearInput })
            )
        );
    }

    exit() {
        this.#store.dispatch(new AuthPageActions.Logout());
    }

    public revealUser(): void {
        this.#store.dispatch(new AuthPageActions.Reveal());
    }
}
